import { MacroRegion } from "enum/MacroRegion";
import { extractCoordinatesFromGeoJSON } from "pages/ForecastChart/ForecastChartPanelMap/util/extractCoordinatesFromGeoJSON";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useStores } from "stores/setup/use-store";
import { getMacroRegionOriginFromPolygon } from "util/macroRegion/getMacroRegionOriginFromPolygon";

export function useAlertGeospatial() {
  const {
    dataStores: {
      forecastChartPageStore,
      insightAlertStore,
      regionStore,
      authStore,
      locationStore
    },
  } = useStores();

  const history = useHistory();
  const location = useLocation();

  const selectedGeofence = useMemo(() => insightAlertStore.selectedGeofence, [insightAlertStore.selectedGeofence]);
  const isAreaDrawingMode = useMemo(() => forecastChartPageStore.isAreaDrawingMode, [forecastChartPageStore.isAreaDrawingMode]);
  const isPointSelectionMode = useMemo(() => forecastChartPageStore.isPointSelectionMode, [forecastChartPageStore.isPointSelectionMode]);
  const isGeofenceInvalid = useMemo(() => insightAlertStore.isGeofenceInvalid, [insightAlertStore.isGeofenceInvalid]);
  const areaSelectorVisible = useMemo(() => insightAlertStore.areaSelectorVisible, [insightAlertStore.areaSelectorVisible]);
  const areas = useMemo(() => locationStore.areas, [locationStore.areas]);
  const selectedStationForAlert = useMemo(() => insightAlertStore.selectedStationForAlert, [insightAlertStore.selectedStationForAlert]);

  useEffect(() => {
    locationStore.getAllLocationsAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if we're coming from insight-alerts page (for creation flow)
  const isFromInsightAlerts = useMemo(() => {
    return location.pathname === "/insight-alerts" ||
      new URLSearchParams(location.search).get("origin") === "insight-alerts";
  }, [location.pathname, location.search]);

  // Check if we're coming from insight-manager page (for event creation flow)
  const isFromInsightManager = useMemo(() => {
    return location.pathname === "/insight-manager" ||
      new URLSearchParams(location.search).get("origin") === "insight-manager";
  }, [location.pathname, location.search]);

  const handleDrawingMode = useCallback(() => {
    forecastChartPageStore.setIsAreaDrawingMode(true);

    // If from insight-manager (event creation), redirect to forecast-charts with origin param
    if (isFromInsightManager) {
      history.push("/forecast-charts?create_alert=true&origin=insight-manager");
    }
    // If from insight-alerts, redirect to forecast-charts with origin param
    else if (isFromInsightAlerts) {
      history.push("/forecast-charts?create_alert=true&origin=insight-alerts");
    }
  }, [forecastChartPageStore, isFromInsightAlerts, isFromInsightManager, history]);

  const handlePointSelectionMode = useCallback(() => {
    // Save the current station as original before entering edit mode
    // This allows restoring it if user cancels
    insightAlertStore.setOriginalStationForAlert(
      insightAlertStore.selectedStationForAlert
    );
    forecastChartPageStore.setIsPointSelectionMode(true);

    // If from insight-manager (event creation), redirect to forecast-charts with origin param
    if (isFromInsightManager) {
      history.push("/forecast-charts?create_alert=true&origin=insight-manager");
    }
    // If from insight-alerts, redirect to forecast-charts with origin param
    else if (isFromInsightAlerts) {
      history.push("/forecast-charts?create_alert=true&origin=insight-alerts");
    }
  }, [forecastChartPageStore, insightAlertStore, isFromInsightAlerts, isFromInsightManager, history]);

  const clearSelectedStation = useCallback(() => {
    insightAlertStore.setSelectedStationForAlert(null);
  }, [insightAlertStore]);

  const handleGeoJsonLoaded = useCallback(
    (geojson: any) => {
      if (!geojson) return;

      insightAlertStore.setIsGeofenceLoadedByGeoJson(true);

      const coordinates = extractCoordinatesFromGeoJSON(geojson);
      const macroRegion = getMacroRegionOriginFromPolygon(
        coordinates,
        regionStore.macroRegions,
        authStore.userMarcroRegions as MacroRegion[]
      );
      insightAlertStore.setSelectedGeofence(coordinates);
      if (macroRegion) {
        insightAlertStore.setMacroRegionOrigin(macroRegion);
        insightAlertStore.setIsGeofenceInvalid(false);
      } else {
        insightAlertStore.setIsGeofenceInvalid(true);
        insightAlertStore.setMacroRegionOrigin(null);
      }
    },
    [authStore.userMarcroRegions, insightAlertStore, regionStore.macroRegions]
  );

  const clearGeofence = useCallback(() => {
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
  }, [insightAlertStore]);

  const exitDrawingMode = useCallback(() => {
    forecastChartPageStore.setIsAreaDrawingMode(false);
  }, [forecastChartPageStore]);

  const setIsGeofenceLoadedByGeoJson = useCallback((value: boolean) => {
    insightAlertStore.setIsGeofenceLoadedByGeoJson(value);
  }, [insightAlertStore]);

  const handleEnableAreaSelector = useCallback((value: boolean) => {
    insightAlertStore.setAreaSelectorVisible(value);
  }, [insightAlertStore]);

  const handleAreaSelection = useCallback(
    (selectedAreaId: number, areas: any[], setFieldValue: (field: string, value: any) => void) => {
      setFieldValue("locationId", selectedAreaId);

      const selectedArea = areas.find((area) => area.id === selectedAreaId);
      if (selectedArea && selectedArea.coordinates) {

        insightAlertStore.setSelectedGeofence(selectedArea.coordinates);
        setFieldValue("geofence", selectedArea.coordinates);
        setFieldValue("area", selectedArea.name);

        const macroRegion = getMacroRegionOriginFromPolygon(
          [selectedArea.coordinates],
          regionStore.macroRegions,
          authStore.userMarcroRegions as MacroRegion[]
        );

        if (macroRegion) {
          insightAlertStore.setMacroRegionOrigin(macroRegion);
          insightAlertStore.setIsGeofenceInvalid(false);
        } else {
          insightAlertStore.setIsGeofenceInvalid(true);
          insightAlertStore.setMacroRegionOrigin(null);
        }
      }
    },
    [insightAlertStore, regionStore.macroRegions, authStore.userMarcroRegions]
  );

  return {
    handleDrawingMode,
    handlePointSelectionMode,
    handleGeoJsonLoaded,
    clearGeofence,
    clearSelectedStation,
    exitDrawingMode,
    selectedGeofence,
    isAreaDrawingMode,
    isPointSelectionMode,
    isGeofenceInvalid,
    setIsGeofenceLoadedByGeoJson: setIsGeofenceLoadedByGeoJson,
    areas,
    handleAreaSelection,
    handleEnableAreaSelector,
    areaSelectorVisible,
    selectedStationForAlert,
  };
}
